// FlameKingManagerDetailPage.tsx — one health snapshot, in full.
// Reached by clicking a row on the Flame King Manager board. Re-uses the list
// endpoint and finds the row by id (the feed is small, capped at 200).

import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { useApi } from "@/lib/api";
import { architectureT as T } from "../blueprint/ArchitectureBlocks";
import type { HealthSnapshot } from "./FlameKingManagerPage";

export function FlameKingManagerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const api = useApi();
  const [row, setRow] = useState<HealthSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api("/api/flameking-health");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as HealthSnapshot[];
        const found = data.find((r) => r.id === id) ?? null;
        if (!cancelled) { setRow(found); setLoading(false); }
      } catch (e) {
        if (!cancelled) { setError(e instanceof Error ? e.message : "Failed to load"); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [api, id]);

  const ok = row?.status === "ok";

  return (
    <div style={{ fontFamily: "Inter, sans-serif", boxSizing: "border-box", padding: "40px 48px 80px" }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <Link to="/flameking-manager" style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          color: T.accent, fontSize: 13, fontWeight: 600, textDecoration: "none", marginBottom: 24,
        }}>
          <ArrowLeft style={{ width: 15, height: 15 }} /> Flame King Manager
        </Link>

        {loading ? (
          <div style={{ padding: 24, fontSize: 14, color: T.ink3, display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> Loading…
          </div>
        ) : error ? (
          <div style={{ padding: "16px 20px", color: T.red, background: T.redSoft, border: `1px solid ${T.red}`, borderRadius: 8, fontSize: 13 }}>
            {error}
          </div>
        ) : !row ? (
          <div style={{ padding: "16px 20px", color: T.ink3, fontSize: 14 }}>Snapshot not found.</div>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
              <span style={{ width: 12, height: 12, borderRadius: 999, background: ok ? T.green : T.red }} />
              <h1 style={{ fontSize: 26, fontWeight: 800, color: T.ink, margin: 0, letterSpacing: "-0.02em" }}>
                {ok ? "Healthy" : "Problem detected"}
              </h1>
            </div>
            <div style={{ fontSize: 13, color: T.ink3, marginBottom: 20 }}>{new Date(row.created_at).toLocaleString()}</div>
            <p style={{ fontSize: 16, color: T.ink, lineHeight: 1.6, marginBottom: 28 }}>{row.summary}</p>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: T.ink3, textTransform: "uppercase", marginBottom: 10 }}>
              Full detail
            </div>
            <pre style={{
              background: T.bg2, border: `1px solid ${T.line}`, borderRadius: 10,
              padding: 20, fontSize: 12.5, lineHeight: 1.6, color: T.ink,
              overflowX: "auto", fontFamily: "ui-monospace, Menlo, monospace", margin: 0,
            }}>
              {JSON.stringify(row.detail, null, 2)}
            </pre>
          </>
        )}
      </div>
    </div>
  );
}

(FlameKingManagerDetailPage as unknown as { path: string }).path = "/flameking-manager/:id";
